import { get, post } from '@/api/lib/amplify-wrapper'
import {
  Feedback,
  FeedbackStats,
  CreateFeedbackRequest,
  FeedbacksListResponse
} from '@/types/performance'
import { createImpersonatedApiHeaders } from '@/lib/services/api-utils'

const API_NAME = 'performance'

/**
 * Helper function to serialize data for API requests
 * Converts Date objects to ISO strings for JSON serialization
 */
const serializeData = (data: any) => {
  return JSON.parse(JSON.stringify(data, (_key, value) => {
    // Convert Date objects to ISO strings
    if (value instanceof Date) {
      return value.toISOString()
    }
    return value
  }))
}

/**
 * Helper function to extract pagination metadata from response headers
 */
const extractPaginationMetadata = (headers: any, itemsLength: number): { nextToken?: string; itemCount: number } => {
  const nextToken = headers?.['x-next-token'] || headers?.['X-Next-Token']
  const itemCount = headers?.['x-item-count'] || headers?.['X-Item-Count']

  return {
    nextToken: nextToken || undefined,
    itemCount: itemCount ? parseInt(itemCount) : itemsLength
  }
}

/**
 * Map API errors to user-friendly messages
 */
const mapApiError = (error: any): string => {
  if (error.response?.status === 400) return 'Invalid input. Please check your data.'
  if (error.response?.status === 403) return 'You do not have permission to perform this action.'
  if (error.response?.status === 404) return 'Resource not found.'
  if (error.response?.status === 500) return 'Server error. Please try again later.'
  return 'An unexpected error occurred. Please try again.'
}

// ============================================================================
// Feedbacks API Functions
// ============================================================================

/**
 * Get current user's feedback statistics
 *
 * Returns aggregated statistics for feedbacks sent and received by the current user,
 * including breakdown by category (Reconhecimento, Construtivo, Crítico).
 * This endpoint follows the /me self-service pattern.
 *
 * @returns Promise resolving to the current user's FeedbackStats
 * @throws Error with user-friendly message if API call fails
 */
export const getFeedbackStats = async (): Promise<FeedbackStats> => {
  try {
    const path = '/performance/feedbacks/me/stats'

    const restOperation = get({
      apiName: API_NAME,
      path,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as FeedbackStats
  } catch (error: any) {
    console.error('Error getting feedback stats:', error)
    const message = mapApiError(error)
    throw new Error(message)
  }
}

/**
 * Get current user's recent feedbacks
 *
 * Returns the most recent feedbacks received by the current user (default: last 10).
 * This endpoint follows the /me self-service pattern.
 *
 * @returns Promise resolving to array of recent Feedbacks
 * @throws Error with user-friendly message if API call fails
 */
export const getRecentFeedbacks = async (): Promise<Feedback[]> => {
  try {
    const path = '/performance/feedbacks/me/recent'

    const restOperation = get({
      apiName: API_NAME,
      path,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    // Backend returns { items: Feedback[], pagination: {...} }
    return (data as any)?.items || []
  } catch (error: any) {
    console.error('Error getting recent feedbacks:', error)
    const message = mapApiError(error)
    throw new Error(message)
  }
}

/**
 * List current user's feedbacks with pagination
 *
 * Returns paginated list of feedbacks sent to or received by the current user.
 * This endpoint follows the /me self-service pattern.
 *
 * @param params - Optional pagination parameters
 * @param params.limit - Maximum number of items to return (default: 20)
 * @param params.nextToken - Token for fetching next page
 * @param params.type - Filter by 'received' (default) or 'sent' feedbacks
 * @returns Promise resolving to paginated list of feedbacks
 * @throws Error with user-friendly message if API call fails
 */
export const listFeedbacksMe = async (params?: {
  limit?: number
  nextToken?: string
  type?: 'received' | 'sent'
}): Promise<FeedbacksListResponse> => {
  try {
    const queryParams = new URLSearchParams()

    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.nextToken) queryParams.append('nextToken', params.nextToken)
    if (params?.type) queryParams.append('type', params.type)

    const path = '/performance/feedbacks/me' + (queryParams.toString() ? `?${queryParams.toString()}` : '')

    const restOperation = get({
      apiName: API_NAME,
      path,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    const items = data as unknown as Feedback[]

    const pagination = extractPaginationMetadata(response.headers, items.length)

    return {
      items,
      pagination
    }
  } catch (error: any) {
    console.error('Error listing feedbacks:', error)
    const message = mapApiError(error)
    throw new Error(message)
  }
}

/**
 * Create a quick feedback
 *
 * Submits a quick feedback using the COIN/SBI methodology. Quick feedbacks have
 * less strict validation than complete feedbacks.
 *
 * @param data - Feedback creation data
 * @returns Promise resolving to the created Feedback
 * @throws Error with user-friendly message if API call fails or validation fails
 */
export const createQuickFeedback = async (data: CreateFeedbackRequest): Promise<Feedback> => {
  try {
    const path = '/performance/feedbacks/quick'

    const restOperation = post({
      apiName: API_NAME,
      path,
      options: {
        body: serializeData(data),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const responseData = await response.body.json()
    return responseData as unknown as Feedback
  } catch (error: any) {
    console.error('Error creating quick feedback:', error)
    const message = mapApiError(error)
    throw new Error(message)
  }
}

/**
 * Create a complete feedback
 *
 * Submits a complete feedback using the COIN/SBI methodology with full validation.
 * All COIN/SBI fields (context, observation, impact, nextSteps) must have minimum
 * 20 characters each.
 *
 * @param data - Feedback creation data (with COIN/SBI fields validated)
 * @returns Promise resolving to the created Feedback
 * @throws Error with user-friendly message if API call fails or validation fails
 */
export const createCompleteFeedback = async (data: CreateFeedbackRequest): Promise<Feedback> => {
  try {
    // Client-side validation for complete feedback
    if (data.type === 'complete') {
      const requiredFields = [
        { field: 'context', value: data.context },
        { field: 'observation', value: data.observation },
        { field: 'impact', value: data.impact },
        { field: 'nextSteps', value: data.nextSteps }
      ]

      for (const { field, value } of requiredFields) {
        if (!value || value.length < 20) {
          throw new Error(`${field} must have at least 20 characters for complete feedback`)
        }
      }
    }

    const path = '/performance/feedbacks/complete'

    const restOperation = post({
      apiName: API_NAME,
      path,
      options: {
        body: serializeData(data),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const responseData = await response.body.json()
    return responseData as unknown as Feedback
  } catch (error: any) {
    console.error('Error creating complete feedback:', error)
    const message = mapApiError(error)
    throw new Error(message)
  }
}
